import CT from '@marketplace-ai/tags';
import { ColDef } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { Column } from '~/components';
import { dateComparator } from '~/components/agGrid/columns/dateColumns';
import { createValidCol } from '~/components/agGrid/columns/sharedColumns';
import { ACTIONS_COLUMN_BASE, ActionsRenderer, type ActionDef } from '~/components/Table/gridUtil';
import { formatCredentialDate } from '~/features/utils';
import { EducationFormValues } from '../types';

const DETAIL_TAGS = CT.academics.education.educationDetailTable;

export const EDUCATION_DETAIL_COLUMNS: Column[] = [
  {
    label: 'Detail Type',
    cyTag: DETAIL_TAGS.label,
    data: (row) => row.fieldLabel.label,
  },
  {
    label: 'Text',
    cyTag: DETAIL_TAGS.text,
    data: (row) =>
      row.fieldTxt?.length > 20 ? row.fieldTxt.substring(0, 20) + '...' : row.fieldTxt,
  },
];

export const CreateEduCols = ({
  handleDelete,
  handleEdit,
}: {
  handleDelete: (id: number) => void;
  handleEdit: (id: number) => void;
}): ColDef[] => [
  { colId: 'id', field: 'id', hide: true },
  createValidCol(),
  {
    colId: 'institution',
    field: 'institution.name.label',
    headerName: 'Institution',
    cellStyle: {
      display: 'flex',
      alignItems: 'center',
      paddingTop: '8px',
      paddingBottom: '8px',
      lineHeight: '1.5',
    },
  },
  {
    colId: 'address',
    headerName: 'Address',
    filter: true,
    cellStyle: {
      display: 'flex',
      alignItems: 'center',
      paddingTop: '8px',
      paddingBottom: '8px',
      lineHeight: '1.5',
    },
    valueGetter: ({ data }) => {
      if (!data) return '';
      return [data.institution.city, data.institution.state.value, data.institution.country.label]
        .filter(Boolean)
        .join(', ');
    },
    comparator: (valueA: string, valueB: string) => valueA.localeCompare(valueB),
  },
  {
    colId: 'attendedDates',
    headerName: 'Attended Dates',
    cellRenderer: ({ data }: CustomCellRendererProps<EducationFormValues>) => {
      if (data) {
        const startDate = formatCredentialDate(data.startDate);
        const endDate = data.endDate ? formatCredentialDate(data.endDate) : 'Present';
        return `${startDate} – ${endDate}`;
      }
    },
    filter: 'agDateColumnFilter',
    valueGetter: ({ data }) => {
      if (data?.startDate) {
        return new Date(data.startDate);
      }
      return null;
    },
    comparator: dateComparator,
  },
  { colId: 'degree', field: 'degree.label', headerName: 'Degree' },
  {
    ...ACTIONS_COLUMN_BASE,
    width: 80,
    cellRenderer: ActionsRenderer,
    cellRendererParams: {
      actions: [
        { type: 'edit', handler: handleEdit, cyTag: CT.privileges.table.deleteBtn },
        { type: 'delete', handler: handleDelete, cyTag: CT.privileges.table.deleteBtn },
      ] satisfies ActionDef[],
    },
  },
];
